import React from 'react';
import { useConfig } from '@cp/shared/config';
import { Article, Box } from '@youhelp-ru/ui';
import { Typography } from '@cp/ds/src/components/typography';
import { Link } from '@cp/ds/src/components/link';
import type { LegalConfig } from '../model';

export const PrivacyPolicyWidget = () => {
  const config = useConfig<LegalConfig>();
  return (
    <Article>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h1" sx={{ m: 0, mb: 2.75 }} variant="h1">
          Политика по обработке персональных данных (Политика конфиденциальности)
        </Typography>
        <p>
          Настоящая Политика обработки персональных данных (далее — Политика) действует в отношении всей информации, которую{' '}
          {config.get('LEGAL_NAME')} (адрес: Россия, {config.get('LEGAL_ADDRESS')}, ИНН {config.get('INN')}, ОГРН {config.get('OGRN')})
          может получить о физических лицах, не являющихся сотрудниками {config.get('LEGAL_NAME')}, при использовании сайта и сервисов
          YouHelp.
        </p>
        <p>
          Политика разработана в соответствии с требованиями законодательства Российской Федерации, включая Федеральный закон «О
          персональных данных», и размещается в открытом доступе.
        </p>
        <p>Сервис YouHelp предоставляет доступ к функционалу платформы на безвозмездной основе.</p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          1. Юридические основания обработки персональных сведений
        </Typography>
        <p>Основаниями для обработки персональных данных являются:</p>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">Федеральный закон «О персональных данных»;</Typography>
          <Typography component="li">согласие субъекта персональных данных;</Typography>
          <Typography component="li">Пользовательское соглашение.</Typography>
        </Typography>
        <p>
          Согласие на обработку персональных данных выражается путем совершения Пользователем действий на сайте (регистрация, авторизация,
          отправка форм).
        </p>
        <p>
          Нажимая кнопку регистрации, авторизации или отправки формы, Пользователь подтверждает, что дает согласие на обработку своих
          персональных данных на условиях настоящей Политики и принимает условия Пользовательского соглашения.
        </p>
        <p>
          {config.get('LEGAL_NAME')} уведомило Роскомнадзор о начале обработки персональных данных и включено в реестр операторов
          персональных данных.
        </p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          2. Согласие субъекта на обработку персональной информации
        </Typography>
        <p>
          При использовании сайта и сервисов YouHelp Пользователь соглашается на обработку своих персональных данных в объеме, необходимом
          для использования функционала сервиса. Пользователь имеет право в любой момент отозвать согласие на обработку персональных данных,
          направив уведомление на электронную почту:{' '}
          <Link external href={`mailto:${config.get('ADMIN_EMAIL')}`}>
            {config.get('ADMIN_EMAIL', '')}
          </Link>
        </p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          3. Цели работы с персональной информацией
        </Typography>
        <p>
          Основная цель – предоставление Пользователю сведений о {config.get('LEGAL_NAME')} (условиях договора, акциях и предложениях),
          анализ качества услуг и улучшение обслуживания клиентов, предоставление услуг, их учет для взаимных расчетов, обработка жалоб и
          претензий от клиентов. Обработка персональных данных может осуществляться в целях аналитики и улучшения пользовательского опыта на
          сайте, включая сбор обезличенных данных через системы веб-аналитики.
        </p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          4. Объем персональной информации для обработки
        </Typography>
        <p>
          {config.get('LEGAL_NAME')} обрабатывает такие данные Пользователей, как ФИО, электронная почта, телефон, адрес проживания и дата
          рождения. На сайте применяются технологии cookie – текстовые файлы, в которых содержится информация о сайтах, посещенных
          Пользователем. Данные помогают собирать информацию о действиях лиц на сайте, что помогает улучшить его качество, расширить
          возможности. В любой момент лицо может изменить настройки своего браузера, чтобы файлы cookie не сохранялись. В этом случае
          возможна некорректная работа некоторых сервисов. На сайте имеются счетчики для аналитики «Яндекс.Метрика», помогающие оценивать
          посещаемость сайта, исследовать поведение Пользователей. Информация, полученная с помощью таких сервисов, носит обезличенный
          характер.
        </p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          5. Конфиденциальность персональной информации
        </Typography>
        <p>Организация не раскрывает другим лицам, не распространяет личные данные кроме случаев, предусмотренных законодательством.</p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          6. Сроки обработки персональных данных
        </Typography>
        <p>
          Персональные данные обрабатываются до достижения целей обработки либо до отзыва согласия Пользователем, если иное не предусмотрено
          законодательством Российской Федерации.
        </p>
        <p>
          После достижения целей обработки персональные данные подлежат уничтожению или обезличиванию в срок не более 30 календарных дней.
        </p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          7. Правомочия Пользователя
        </Typography>
        <p>
          Субъект может направить запрос {config.get('LEGAL_NAME')} на получение сведений, связанных с обработкой его персональной
          информации по ст. 14 ФЗ «О персональных данных». Запрос направляется по адресу {config.get('LEGAL_ADDRESS')}.
        </p>
      </Box>
      <Box sx={{ mb: 2.75 }}>
        <Typography component="h2" sx={{ m: 0, mb: 2.75 }} variant="h2">
          8. Защита персональной информации
        </Typography>
        <Typography component="ul" sx={{ my: 2 }}>
          <Typography component="li">Назначается сотрудник, ответственный за работу с такими данными.</Typography>
          <Typography component="li">Разрабатываются локальные акты.</Typography>
          <Typography component="li">
            Применяются меры технического, организационного и правового характера (определяются потенциальные угрозы безопасности,
            устанавливаются права доступа к информации, регистрируются действия, которые совершаются с данными, в специальной информационной
            системе).
          </Typography>
          <Typography component="li">
            Контроль за мерами по обеспечению безопасности данных и защищенности информационной системы.
          </Typography>
          <Typography component="li">
            Оценка потенциального вреда, который может быть причинен субъекту при нарушении закона о персональных данных, соотношение ущерба
            и мероприятий, проводимых {config.get('LEGAL_NAME')}, по выполнению своих обязанностей.
          </Typography>
          <Typography component="li">Предотвращение несанкционированного доступа к носителям персональной информации.</Typography>
          <Typography component="li">
            Сотрудники, работающие с такой информацией, ознакомлены с законодательством о персональных данных, локальными актами по вопросам
            их обработки и защиты. Для сохранения конфиденциальности информации организация формирует современную ИТ-среду, принимает меры
            по защите от незаконного доступа.
          </Typography>
        </Typography>
        <p>{config.get('LEGAL_NAME')} регулярно проверяет эффективность применяемых мер защиты персональных данных.</p>
      </Box>
    </Article>
  );
};
